import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { WindowProvider, useIsMobile, useWindows } from "./WindowManager";
import APPS from "./apps";
import { QUICK_FILES } from "./fs";
import Desktop from "./components/Desktop";
import Taskbar from "./components/Taskbar";
import StartMenu from "./components/StartMenu";
import { MenuProvider } from "./components/ContextMenu";
import { BootScreen, LockScreen } from "./components/Screens";
import SeoContent from "./components/SeoContent";
import SourceToast from "./components/SourceToast";
import "./win11.css";

const BOOT_MS = 1500;
const SESSION_KEY = "win11-session-started";

export default function Win11() {
  const mobile = useIsMobile();
  const surfaceRef = useRef(null);
  const [surface, setSurface] = useState({ left: 0, top: 0, w: 0, h: 0 });
  const [stage, setStage] = useState(() => {
    if (typeof window === "undefined") return "desktop";
    // Boot + lock are decorative: skip them for repeat visits in the same
    // session and for anyone who asked for reduced motion.
    const skip =
      sessionStorage.getItem(SESSION_KEY) ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return skip ? "desktop" : "boot";
  });
  const [startOpen, setStartOpen] = useState(false);

  /* Body owns the Win11 look so the desktop can fill the viewport exactly. */
  useEffect(() => {
    document.body.classList.add("win11");
    return () => document.body.classList.remove("win11");
  }, []);

  useEffect(() => {
    if (stage !== "boot") return;
    const id = setTimeout(() => setStage("lock"), BOOT_MS);
    return () => clearTimeout(id);
  }, [stage]);

  const unlock = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setStage("desktop");
  }, []);

  /* Track the desktop surface (viewport minus taskbar) for window geometry. */
  useLayoutEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSurface({
        left: r.left,
        top: r.top,
        w: Math.round(r.width),
        h: Math.round(r.height),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("scroll", measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", measure, true);
    };
  }, [stage]);

  return (
    <WindowProvider apps={APPS} surface={surface}>
      <MenuProvider>
      <SeoContent />

      <div className="win-shell">
        <Desktop mobile={mobile} surfaceRef={surfaceRef} />
        <StartMenu
          open={startOpen}
          onClose={() => setStartOpen(false)}
          onLock={() => {
            setStartOpen(false);
            setStage("lock");
          }}
        />
        <Taskbar
          mobile={mobile}
          startOpen={startOpen}
          onToggleStart={() => setStartOpen((v) => !v)}
        />
        <SourceToast ready={stage === "desktop"} />
      </div>

      {stage === "boot" && <BootScreen />}
      {stage === "lock" && <LockScreen onUnlock={unlock} />}

        <AutoOpen ready={stage === "desktop" && surface.w > 0} />
      </MenuProvider>
    </WindowProvider>
  );
}

/**
 * Opens File Explorer on the portfolio folder once the desktop is measured, so
 * a first-time visitor lands somewhere with something in it. Skipped when the tab already has a session to restore —
 * a refresh should give back exactly the windows that were open.
 */
function AutoOpen({ ready }) {
  const { open, hydrated } = useWindows();
  const done = useRef(false);

  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    if (!hydrated) open("explorer", { folderId: QUICK_FILES.portfolio.id });
  }, [ready, hydrated, open]);

  return null;
}
